import {EventProcessStageTitle, Tag} from '@hconnect/common/components'
import {getAssigneesValue} from '@hconnect/common/utils'
import {DateFormat} from '@hconnect/uikit'
import {Grid} from '@mui/material'
import {getEquipmentLabelDetails, EventStatusLabel} from '@widgets/common-shift-event'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {OilAnalysisActionDto} from '../../types'

import {TaskDescription} from './TaskDescription'

type Props = {
  oilAnalysisAction: OilAnalysisActionDto
  plantId: string
  plantName: string
  timezone: string
}

export const OilAnalysisActionDetails = ({
  oilAnalysisAction,
  plantId,
  plantName,
  timezone
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const translate = (key: string) => t(key)
  const noneOfThatLabel = translate('task.noneOfThat')

  const assigneesValue: string = useMemo(() => {
    const assignees = getAssigneesValue(oilAnalysisAction.assignees ?? [], t)
    return assignees ? assignees : noneOfThatLabel
  }, [noneOfThatLabel, oilAnalysisAction, t])

  const criticality = oilAnalysisAction.oilAnalysisProperties?.criticality
  const criticalityValue = criticality ?? noneOfThatLabel

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Tag label={translate('task.label.status')} data-test-id="task-details-status">
            <EventStatusLabel status={oilAnalysisAction.status} />
          </Tag>
        </Grid>
        <Grid item md={12}>
          <Tag label={translate('task.label.priority')} data-test-id="task-details-priority">
            {oilAnalysisAction.priority
              ? translate(`oilAnalysisActions.priority.${oilAnalysisAction.priority}`)
              : noneOfThatLabel}
          </Tag>
        </Grid>

        <Grid item md={12}>
          <Tag label={translate('task.label.dueDate')} data-test-id="task-details-dueDate">
            {oilAnalysisAction.dueDate ? (
              <DateFormat date={oilAnalysisAction.dueDate} timezone={timezone} />
            ) : (
              noneOfThatLabel
            )}
          </Tag>
        </Grid>
        <Grid item md={12}>
          <Tag label={translate('task.label.plant')} data-test-id="task-details-plant">
            {plantName}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            data-test-id="task-details-mainEquipment"
            label={translate('task.label.mainEquipment')}
          >
            {getEquipmentLabelDetails(oilAnalysisAction.mainEquipment, false)}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag data-test-id="task-details-equipment" label={translate('task.label.equipment')}>
            {getEquipmentLabelDetails(oilAnalysisAction.equipment)}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            label={translate('task.label.processStage')}
            data-test-id="task-details-processStage"
          >
            {oilAnalysisAction.processStage ? (
              <EventProcessStageTitle plantId={plantId} stage={oilAnalysisAction.processStage} />
            ) : (
              noneOfThatLabel
            )}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag data-test-id="task-details-assignees" label={translate('task.label.assignee')}>
            {assigneesValue}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag data-test-id="task-details-criticality" label={translate('task.label.criticality')}>
            {criticalityValue}
          </Tag>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Tag
            label={translate('task.label.description')}
            descriptionSx={{fontWeight: 400, fontSize: 14}}
            data-test-id="task-details-description"
          >
            <TaskDescription description={oilAnalysisAction.description} />
          </Tag>
        </Grid>
      </Grid>
    </>
  )
}
